import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { X, AlertCircle, Loader2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CANCEL_REASONS = [
  { value: 'schedule_conflict', label: 'Schedule conflict - need to prioritize another commitment' },
  { value: 'need_documents', label: 'Need more time to gather financial documents' },
  { value: 'internal_review', label: 'Waiting on internal team or business partner approval' },
  { value: 'timing_not_right', label: 'The timing isn\'t right for our business currently' },
  { value: 'prefer_different_format', label: 'Would prefer to communicate via email or phone instead' },
  { value: 'personal_matter', label: 'Personal or family matter came up' },
  { value: 'other', label: 'Other reason' }
];

export function CancelForm({ 
  token, 
  scheduledDate,
  scheduledTime,
  onSuccess, 
  onCancel,
  onRescheduleInstead
}) {
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason for cancellation');
      return;
    }

    if (reason === 'other' && !otherReason.trim()) {
      toast.error('Please provide your reason');
      return;
    }

    if (!confirmed) {
      toast.error('Please confirm your cancellation');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/v1/consultations/${token}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: reason,
          other_reason: reason === 'other' ? otherReason : null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Consultation cancelled successfully');
        onSuccess?.(data);
      } else {
        throw new Error(data.detail || 'Failed to cancel');
      }
    } catch (err) {
      console.error('Cancel error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="cancel-form">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <X className="w-5 h-5 text-red-500" />
          Cancel Consultation
        </CardTitle>
        {scheduledDate && scheduledTime && (
          <p className="text-sm text-gray-500">
            Scheduled: {scheduledDate} at {scheduledTime} ET
          </p>
        )}
      </CardHeader>

      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Notice */}
        <Alert className="mb-6 border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Before you cancel:</strong> If you need to reschedule instead, please use the "Reschedule" option on the previous screen. We're always happy to find a time that works better for you.
          </AlertDescription>
        </Alert>

        {/* Reason Selection */}
        <div className="mb-6">
          <Label className="mb-3 block">Please let us know why you're cancelling <span className="text-gray-400 font-normal">(helps us improve)</span></Label>
          <RadioGroup value={reason} onValueChange={setReason}>
            {CANCEL_REASONS.map((r) => (
              <div key={r.value} className="flex items-center space-x-2 py-2">
                <RadioGroupItem 
                  value={r.value} 
                  id={r.value}
                  data-testid={`reason-${r.value}`}
                />
                <Label htmlFor={r.value} className="font-normal cursor-pointer">
                  {r.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Other Reason Input */}
        {reason === 'other' && (
          <div className="mb-6">
            <Label className="mb-2 block">Please explain</Label>
            <Textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="Tell us why you're cancelling..."
              className="h-24"
              data-testid="other-reason-input"
            />
          </div>
        )}

        {/* Confirmation Checkbox */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-gray-300"
              data-testid="confirm-checkbox"
            />
            <span className="text-sm text-gray-700">
              I confirm I want to cancel this consultation. I understand I can always book a new appointment when I'm ready.
            </span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button data-testid="cancel-form-button-1"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
            disabled={submitting}
          >
            Go Back
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-red-600 hover:bg-red-700"
            disabled={submitting || !reason || !confirmed}
            data-testid="confirm-cancel-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling...</>
            ) : (
              <><X className="w-4 h-4 mr-2" /> Confirm Cancellation</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default CancelForm;
